/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Brain, Play, Volume2 } from 'lucide-react';
import { audioEngine, IntervalCalculator } from '../../audio/AudioEngine';

interface EarTrainingTestProps {
  currentKey: string;
  currentScale: string;
  noteCollection: any;
}

const EarTrainingTest: React.FC<EarTrainingTestProps> = ({
  currentKey,
  currentScale,
  noteCollection
}) => {
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [questions, setQuestions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<string[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);

  // Initialize audio
  useEffect(() => {
    audioEngine.initialize().catch(console.error);
  }, []);

  // Test intervals (no visual hints - pure ear training)
  const testIntervals = ['P5', 'P4', 'M3', 'm3', 'M2', 'm2'];

  // Start a new test session
  const startTestSession = () => {
    const newQuestions = [];
    
    // Generate 10 random questions
    for (let i = 0; i < 10; i++) {
      const rootNotes = ['C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4'];
      const randomRoot = rootNotes[Math.floor(Math.random() * rootNotes.length)];
      const randomInterval = testIntervals[Math.floor(Math.random() * testIntervals.length)];
      
      newQuestions.push({
        id: i,
        rootNote: randomRoot,
        targetInterval: randomInterval,
        correctAnswer: IntervalCalculator.calculateIntervalNote(randomRoot, randomInterval)
      });
    }
    
    setQuestions(newQuestions);
    setAnswers(new Array(10).fill(''));
    setCurrentQuestion(0);
    setIsSessionActive(true);
  };

  // Play current question
  const playCurrentQuestion = async () => {
    if (!questions[currentQuestion] || isPlaying) return;
    
    const question = questions[currentQuestion];
    setIsPlaying(true);
    
    try {
      await audioEngine.playInterval(
        question.rootNote,
        question.correctAnswer,
        'melodic',
        { duration: 1200 }
      );
    } catch (error) {
      console.error('Audio playback error:', error);
    } finally {
      setIsPlaying(false);
    }
  };

  // Submit answer
  const submitAnswer = (interval: string) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = interval;
    setAnswers(newAnswers);

    // Auto-advance to next question
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(prev => prev + 1);
      }, 500);
    }
  };

  // Calculate results
  const calculateResults = () => {
    let correct = 0;
    questions.forEach((q, index) => {
      if (answers[index] === q.targetInterval) {
        correct++;
      }
    });
    return { correct, total: questions.length };
  };

  // Finish test
  const finishTest = () => {
    setIsSessionActive(false);
  };

  // Reset test
  const resetTest = () => {
    setIsSessionActive(false);
    setQuestions([]);
    setAnswers([]);
    setCurrentQuestion(0);
  };

  if (!isSessionActive) {
    const results = questions.length > 0 ? calculateResults() : null;
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Ear Training Test
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {results ? (
            // Show results
            <div className="text-center space-y-4">
              <div className="space-y-2">
                <h3 className="text-2xl font-bold">Test Complete!</h3>
                <div className="text-4xl font-bold text-blue-600">
                  {results.correct}/{results.total}
                </div>
                <p className="text-lg text-muted-foreground">
                  {Math.round((results.correct / results.total) * 100)}% Accuracy
                </p>
              </div>

              {/* Performance feedback */}
              <Card className="p-4">
                <div className="space-y-3">
                  <h4 className="font-semibold">Detailed Results</h4>
                  <div className="space-y-1">
                    {questions.map((q, index) => (
                      <div key={q.id} className="flex items-center justify-between text-sm">
                        <span>Question {index + 1}: {q.rootNote} → {q.targetInterval}</span>
                        <Badge variant={answers[index] === q.targetInterval ? "default" : "destructive"}>
                          {answers[index] === q.targetInterval ? "✓" : "✗"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              <div className="flex gap-2">
                <Button onClick={startTestSession} className="flex-1">
                  New Test
                </Button>
                <Button variant="outline" onClick={resetTest} className="flex-1">
                  Back to Start
                </Button>
              </div>
            </div>
          ) : (
            // Start screen
            <div className="text-center space-y-6">
              <div className="space-y-2">
                <h3 className="text-xl font-semibold">Ready for a challenge?</h3>
                <p className="text-muted-foreground">
                  Test your interval recognition with no visual hints
                </p>
              </div>

              <Card className="p-4 bg-amber-50 border-amber-200">
                <div className="space-y-2">
                  <h4 className="font-semibold text-amber-900">Test Format</h4>
                  <ul className="text-sm text-amber-800 space-y-1 text-left">
                    <li>• 10 random interval questions</li>
                    <li>• No visual hints on fretboard</li>
                    <li>• Pure ear training - listen and identify</li>
                    <li>• Intervals: P5, P4, M3, m3, M2, m2</li>
                  </ul>
                </div>
              </Card>

              <Button size="lg" onClick={startTestSession}>
                <Brain className="h-5 w-5 mr-2" />
                Start Test
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Test session active
  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Question {currentQuestion + 1} of {questions.length}
          </CardTitle>
          <Badge variant="outline">
            {Math.round(progress)}% Complete
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Question */}
        <div className="text-center space-y-6">
          <div className="space-y-2">
            <h3 className="text-xl font-semibold">Listen carefully...</h3>
            <p className="text-muted-foreground">
              What interval do you hear?
            </p>
          </div>

          <Button 
            size="lg" 
            onClick={playCurrentQuestion}
            disabled={isPlaying}
            className="px-8"
          >
            {isPlaying ? (
              <>
                <Volume2 className="h-5 w-5 mr-2 animate-pulse" />
                Playing...
              </>
            ) : (
              <>
                <Play className="h-5 w-5 mr-2" />
                Play Interval
              </>
            )}
          </Button>

          {/* Answer choices */}
          <div className="grid grid-cols-2 gap-3">
            {testIntervals.map((interval) => (
              <Button
                key={interval}
                variant={answers[currentQuestion] === interval ? "default" : "outline"}
                onClick={() => submitAnswer(interval)}
                disabled={answers[currentQuestion] !== ''}
                className="h-16 text-left flex flex-col items-center justify-center"
              >
                <span className="font-semibold">{interval}</span>
                <span className="text-xs opacity-75">
                  {IntervalCalculator.getIntervalName(interval).split(' ')[0]}
                </span>
              </Button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button 
              variant="ghost"
              onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
              disabled={currentQuestion === 0}
            >
              Previous
            </Button>
            
            {currentQuestion === questions.length - 1 ? (
              <Button onClick={finishTest} disabled={answers[currentQuestion] === ''}>
                Finish Test
              </Button>
            ) : (
              <Button 
                onClick={() => setCurrentQuestion(prev => prev + 1)}
                disabled={answers[currentQuestion] === ''}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EarTrainingTest;